import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Button } from '../Wrappers';
import ReactMarkdown from "react-markdown";
import ttAPI from '../../api/ttAPI';
import { sendNotification } from '../../context/UserContext';
import useStyles from '../../pages/customers/styles';
import config from '../../config';

const emptyPolicy = (customerId) => ({
  customer_id: customerId,
  change_reason: '',
  policy_title: '',
  scheduler_duties: '',
  technician_duties: '',
  scope_test_only: '',
  scope_do_not_test: '',
  scope_service_provided_heading: '',
  scope_service_not_provided_heading: '',
  test_interval_3_months: '',
  test_interval_6_months: '',
  test_interval_1_year: '',
  test_interval_2_years: '',
  test_interval_5_years: '',
  failed_item_policy: '',
});

const PolicyTextField = ({ label, name, value, onChange, helperText, required }) => (
  <TextField
    required={required}
    label={label}
    name={name}
    value={value || ''}
    onChange={onChange}
    helperText={helperText}
    fullWidth
    multiline
    rows={5}
    margin="normal"
    variant="outlined"
    inputProps={{
      style: {
        resize: 'vertical'
      }
    }}
  />
);

function formatPreviewMarkdown(value) {
  if (!value) return value;

  return value
    .replace(/<span class="red">(.*?)<\/span>/gi, '**🔴 $1 🔴**')
    .replace(/<span class="green">(.*?)<\/span>/gi, '**🟢 $1 🟢**')
    .replace(/<span class="orange">(.*?)<\/span>/gi, '**🟠 $1 🟠**')
    .replace(/<span class="yellow">(.*?)<\/span>/gi, '**🟡 $1 🟡**')
    .replace(/<span class="blue">(.*?)<\/span>/gi, '**🔵 $1 🔵**');
}

function isNotApplicable(value) {
  if (!value) return false;

  const trimmedValue = value.trim();

  return (
    trimmedValue.toLowerCase() === 'na' ||
    trimmedValue.toLowerCase() === 'n/a'
  );
}

const PreviewBlock = ({ title, value }) => (
  <Box mb={2}>
    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
      {title}
    </Typography>

    <Paper
      variant="outlined"
      style={{
        padding: 12,
        minHeight: 70,
        background: '#fafafa'
      }}
    >
      {value ? (
        <ReactMarkdown>{formatPreviewMarkdown(value)}</ReactMarkdown>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No content yet.
        </Typography>
      )}
    </Paper>
  </Box>
);

export default function CustomerPolicyEditor({ customer, onClose }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [editPolicy, setEditPolicy] = useState(emptyPolicy(customer ? customer.id : 0));

  async function loadPolicy() {
    if (!customer || !customer.id) return;
    setLoading(true);
    try {
      const current = await ttAPI.getCurrentCustomerPolicy(customer.id);
      const versions = await ttAPI.getCustomerPolicyHistory(customer.id);
      setCurrentPolicy(current);
      const versionList = Array.isArray(versions) ? versions : [];
      setSelectedVersionId(
        current && current.id && versionList.some(v => v.id === current.id)
          ? current.id
          : ''
      );
      setHistory(versionList);

      setEditPolicy({
        customer_id: customer.id,
        change_reason: '',
        policy_title: current ? (current.policy_title || '') : '',
        scheduler_duties: current ? (current.scheduler_duties || '') : '',
        technician_duties: current ? (current.technician_duties || '') : '',
        scope_test_only: current ? (current.scope_test_only || '') : '',
        scope_do_not_test: current ? (current.scope_do_not_test || '') : '',
        scope_service_provided_heading: current ? (current.scope_service_provided_heading || '') : '',
        scope_service_not_provided_heading: current ? (current.scope_service_not_provided_heading || '') : '',
        test_interval_3_months: current ? (current.test_interval_3_months || '') : '',
				test_interval_6_months: current ? (current.test_interval_6_months || '') : '',
				test_interval_1_year: current ? (current.test_interval_1_year || '') : '',
				test_interval_2_years: current ? (current.test_interval_2_years || '') : '',
				test_interval_5_years: current ? (current.test_interval_5_years || '') : '',
        failed_item_policy: current ? (current.failed_item_policy || '') : '',
      });
    } catch (e) {
      sendNotification('error', 'Error - Unable to load customer policy.', classes);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer && customer.id]);

  function handleChange(event) {
    const { name, value } = event.target;
    setEditPolicy({ ...editPolicy, [name]: value });
  }

  async function handleVersionChange(event) {
    const versionId = event.target.value;
    setSelectedVersionId(versionId);
    if (!versionId) return;

    try {
      const policy = await ttAPI.getCustomerPolicyVersion(versionId);
      if (policy) {
        setCurrentPolicy(policy);
        setEditPolicy({
          customer_id: customer.id,
          change_reason: '',
          policy_title: policy.policy_title || '',
          scheduler_duties: policy.scheduler_duties || '',
          technician_duties: policy.technician_duties || '',
          scope_test_only: policy.scope_test_only || '',
          scope_do_not_test: policy.scope_do_not_test || '',
          scope_service_provided_heading: policy.scope_service_provided_heading || '',
          scope_service_not_provided_heading: policy.scope_service_not_provided_heading || '',
          test_interval_3_months: policy.test_interval_3_months || '',
  				test_interval_6_months: policy.test_interval_6_months || '',
  				test_interval_1_year: policy.test_interval_1_year || '',
  				test_interval_2_years: policy.test_interval_2_years || '',
  				test_interval_5_years: policy.test_interval_5_years || '',
          failed_item_policy: policy.failed_item_policy || '',
        });
      }
    } catch (e) {
      sendNotification('error', 'Error - Unable to load selected policy version.', classes);
    }
  }

  async function createNewVersion(openPdfAfterSave = false) {
    if (saving) return;
    const requiredFields = [
      ['policy_title', 'Policy title is required.'],
      ['change_reason', 'Change reason is required.'],
      ['scheduler_duties', 'Scheduler duties are required.'],
      ['technician_duties', 'Technician duties are required.'],
      ['scope_test_only', 'Scope - Test Only is required.'],
      ['scope_do_not_test', 'Scope - Do Not Test is required.'],
      ['scope_service_provided_heading', 'Service Provided heading is required.'],
      ['scope_service_not_provided_heading', 'Service Not Provided heading is required.'],
      ['test_interval_3_months', '3 Month test interval is required.'],
      ['test_interval_6_months', '6 Month test interval is required.'],
      ['test_interval_1_year', '1 Year test interval is required.'],
      ['test_interval_2_years', '2 Year test interval is required.'],
      ['test_interval_5_years', '5 Year test interval is required.'],
      ['failed_item_policy', 'Failed item policy is required.'],
    ];

    for (let i = 0; i < requiredFields.length; i++) {
      const [field, message] = requiredFields[i];

      if (!editPolicy[field] || editPolicy[field].trim() === '') {
        sendNotification('error', message, classes);
        return;
      }
    }

    setSaving(true);

    try {
      const response = await ttAPI.createCustomerPolicyVersion(editPolicy);
      if (response && response.ok) {
        sendNotification('success', 'Success - Customer policy version created.', classes);
        await loadPolicy();

        if (openPdfAfterSave && customer && customer.id) {
          window.open(`${config.clientApi}/report/customer-policy?customer_id=${customer.id}`, '_blank');
        }
      } else {
        sendNotification('error', response && response.message ? response.message : 'Error - Unable to create policy version.', classes);
      }
    } catch (e) {
      sendNotification('error', 'Error - Unable to create policy version.', classes);
    } finally {
      setSaving(false);
    }
  }

  const canSave =
    editPolicy.policy_title &&
    editPolicy.policy_title.trim() &&
    editPolicy.change_reason &&
    editPolicy.change_reason.trim() &&
    editPolicy.scheduler_duties &&
    editPolicy.scheduler_duties.trim() &&
    editPolicy.technician_duties &&
    editPolicy.technician_duties.trim() &&
    editPolicy.scope_test_only &&
    editPolicy.scope_test_only.trim() &&
    editPolicy.scope_do_not_test &&
    editPolicy.scope_do_not_test.trim() &&
    editPolicy.scope_service_provided_heading &&
    editPolicy.scope_service_provided_heading.trim() &&
    editPolicy.scope_service_not_provided_heading &&
    editPolicy.scope_service_not_provided_heading.trim() &&
    editPolicy.test_interval_3_months &&
    editPolicy.test_interval_3_months.trim() &&
    editPolicy.test_interval_6_months &&
    editPolicy.test_interval_6_months.trim() &&
    editPolicy.test_interval_1_year &&
    editPolicy.test_interval_1_year.trim() &&
    editPolicy.test_interval_2_years &&
    editPolicy.test_interval_2_years.trim() &&
    editPolicy.test_interval_5_years &&
    editPolicy.test_interval_5_years.trim() &&
    editPolicy.failed_item_policy &&
    editPolicy.failed_item_policy.trim();

  return (
    <Box>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        {customer.name} Customer Policy
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Create a new version when the customer policy changes. If this customer has no policy yet, the system default policy is shown as a starting point.
      </Typography>

      <Box mt={2} mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              variant="outlined"
              margin="normal"
              label="Policy Version"
              value={selectedVersionId}
              onChange={handleVersionChange}
              helperText={
                  history.length > 0
                      ? "Select a saved customer policy version to view/copy from."
                      : "This customer has no policy yet. The TESTntel default template is loaded below but has not been saved for this customer."
              }
            >
              {history.length === 0 && (
                  <MenuItem value="">No policy yet</MenuItem>
              )}

              {history.map((policy) => (
                  <MenuItem key={policy.id} value={policy.id}>
                      {`V${policy.version_no} - ${policy.change_reason || 'No reason'} (${policy.created_by_name || 'Unknown'} • ${policy.createdate || ''})`}
                  </MenuItem>
              ))}
            </TextField>

            <Box mt={1}>
              <Button
                  disabled={!currentPolicy}
                  onClick={() =>
                      window.open(
                          `${config.clientApi}/report/customer-policy?customer_id=${customer.id}`,
                          '_blank'
                      )
                  }
              >
                  Generate Current PDF
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            {currentPolicy && (
              <Box mt={2}>
                <Typography variant="body2">
                  <strong>Viewing:</strong>{' '}
                  {history.length === 0
                    ? `Default Template V${currentPolicy.version_no} - not saved for this customer yet`
                    : `Customer Policy V${currentPolicy.version_no}`}
                </Typography>
                <Typography variant="body2"><strong>Reason:</strong> {currentPolicy.change_reason}</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <TextField
            label="Policy Title"
            name="policy_title"
            required
            value={editPolicy.policy_title || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            helperText="Example: EVT Test Policy 2025"
            />
          <TextField
            label="Change Reason"
            name="change_reason"
            required
            value={editPolicy.change_reason}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            helperText="Example: Initial policy, updated PO process, revised technician instructions."
          />

          <PolicyTextField
            label="Scheduler Duties"
            name="scheduler_duties"
            required
            value={editPolicy.scheduler_duties}
            onChange={handleChange}
            helperText="Simple Markdown: bullets, bold, links. Example: - **Request PO** before scheduling"
          />
          <PolicyTextField
            label="Technician Duties"
            name="technician_duties"
            required
            value={editPolicy.technician_duties}
            onChange={handleChange}
          />
          <TextField
            label="Scope Heading - Service Provided"
            name="scope_service_provided_heading"
            required
            value={editPolicy.scope_service_provided_heading || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            helperText='Required. Use "READ BELOW" if there is no specific service heading.'
          />
          <PolicyTextField
            label="Scope of Testing - Test Only"
            name="scope_test_only"
            required
            value={editPolicy.scope_test_only}
            onChange={handleChange}
          />
          <TextField
            label="Scope Heading - Service Not Provided"
            name="scope_service_not_provided_heading"
            required
            value={editPolicy.scope_service_not_provided_heading || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            helperText='Required. Use "READ BELOW" if there is no specific service heading.'
          />
          <PolicyTextField
            label="Scope of Testing - Do Not Test"
            name="scope_do_not_test"
            required
            value={editPolicy.scope_do_not_test}
            onChange={handleChange}
          />

          <PolicyTextField required label="Test Interval 3 Months" name="test_interval_3_months" value={editPolicy.test_interval_3_months} onChange={handleChange} helperText='Enter "N/A" if this interval does not apply. It will not appear in the PDF.'/>
          <PolicyTextField required label="Test Interval 6 Months" name="test_interval_6_months" value={editPolicy.test_interval_6_months} onChange={handleChange} helperText='Enter "N/A" if this interval does not apply. It will not appear in the PDF.'/>
          <PolicyTextField required label="Test Interval 1 Year" name="test_interval_1_year" value={editPolicy.test_interval_1_year} onChange={handleChange} helperText='Enter "N/A" if this interval does not apply. It will not appear in the PDF.'/>
          <PolicyTextField required label="Test Interval 2 Years" name="test_interval_2_years" value={editPolicy.test_interval_2_years} onChange={handleChange} helperText='Enter "N/A" if this interval does not apply. It will not appear in the PDF.'/>
          <PolicyTextField required label="Test Interval 5 Years" name="test_interval_5_years" value={editPolicy.test_interval_5_years} onChange={handleChange} helperText='Enter "N/A" if this interval does not apply. It will not appear in the PDF.'/>
          <PolicyTextField
            required
            label="Failed Item Policy"
            name="failed_item_policy"
            value={editPolicy.failed_item_policy}
            onChange={handleChange}
          />


            <Box mt={2} display="flex" justifyContent="flex-end">
                <Box mr={2}>
                  <Button
                    onClick={() => createNewVersion(false)}
                    disabled={saving || !canSave}
                  >
                    Save
                  </Button>
                </Box>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => createNewVersion(true)}
                  disabled={saving || !canSave}
                  >
                  Save & Open PDF
                </Button>
            </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box mt={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Preview</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Live Markdown preview.
            </Typography>
            <PreviewBlock title="Scheduler Duties" value={editPolicy.scheduler_duties} />
            <PreviewBlock title="Technician Duties" value={editPolicy.technician_duties} />
            <PreviewBlock title="Scope - Service Provided Heading" value={editPolicy.scope_service_provided_heading} />
            <PreviewBlock title="Scope - Test Only" value={editPolicy.scope_test_only} />
            <PreviewBlock title="Scope - Service NOT Provided Heading" value={editPolicy.scope_service_not_provided_heading} />
            <PreviewBlock title="Scope - Do Not Test" value={editPolicy.scope_do_not_test} />
            {!isNotApplicable(editPolicy.test_interval_3_months) && (
              <PreviewBlock
                title="Test Interval 3 Months"
                value={editPolicy.test_interval_3_months}
              />
            )}

            {!isNotApplicable(editPolicy.test_interval_6_months) && (
              <PreviewBlock
                title="Test Interval 6 Months"
                value={editPolicy.test_interval_6_months}
              />
            )}

            {!isNotApplicable(editPolicy.test_interval_1_year) && (
              <PreviewBlock
                title="Test Interval 1 Year"
                value={editPolicy.test_interval_1_year}
              />
            )}

            {!isNotApplicable(editPolicy.test_interval_2_years) && (
              <PreviewBlock
                title="Test Interval 2 Years"
                value={editPolicy.test_interval_2_years}
              />
            )}

            {!isNotApplicable(editPolicy.test_interval_5_years) && (
              <PreviewBlock
                title="Test Interval 5 Years"
                value={editPolicy.test_interval_5_years}
              />
            )}
            <PreviewBlock title="Failed Item Policy" value={editPolicy.failed_item_policy} />
          </Box>
        </Grid>
      </Grid>

    </Box>
  );
}
